/**
 * Vận hành 24/10 (lee trang 6) — Ô THÔNG SỐ ĐÈN ĐANG CHỌN: bấm một chấm đèn trên bản đồ hoặc một dòng bảng đèn của tuyến.
 *   Số đọc từ CÙNG dòng `useLopNema` (10 s/lần, lệnh xong thì `['nema']` làm mới) — không gọi API riêng.
 *   Luật số 0: NULL ⇒ "—". Mất kết nối ⇒ xám. Không mã đèn (hex) trên ô.
 *   TK-46 §1–§2 (lee 25/10):
 *     · Ô chỉ còn SỐ ĐO + Nháy + "Mở trong Quản lý đèn". Dim của đèn ở khối "Điều khiển độ sáng" (phạm vi Đèn) — một chỗ.
 *     · Đèn KHÔNG có số hiện hành ⇒ MỘT dòng "Chưa có số đo · <lý do>" thay 6 ô "—".
 *     · Một chữ một lần: lý do đã ở chip (vd "Ngõ ra tắt") thì không lặp ở dòng số đo, không lặp cạnh nút Nháy.
 *     · `onThu` (tấm trượt dưới bản đồ, màn < 900 px): nút thu gọn ⇒ `ODenGon` một hàng (tên · trạng thái · % · mở rộng).
 *   KA-49 §4.2 (mẫu `mau_van_hanh` — "Chi tiết đèn"): đồng hồ kim U (cung gradient) · ô I / P / PF / Dim đèn báo (vàng khi lệch lệnh)
 *     · tuổi số đo · relay · khung lỗi · tab Điện · GPS · Định danh · đồ thị P 30 phút dạng miền (từ `NemaSensorLog`, không có ⇒
 *     "chưa có lịch sử") có vạch lúc ra lệnh dim. Số chi tiết (relay, khung, src, gps, iid) đọc `useNemaTu` của tủ — NULL ⇒ "—".
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Badge } from '../ui';
import { NHAN_BADGE, chuLyDo, khoaLenhDen, tenDen } from '../../lib/nemaNhan';
import { tuoiNgan, useNema24h, useNemaTu } from '../../lib/nemaApi';
import { lechGioMs } from '../../lib/giomaychu';
import { KIEU_GOI_Y, defsMien, toMien, useIdMien } from '../bieudo/Mien';
import { iMa, lucCapNhat, pfHai } from '../../lib/vanHanh';
import { useAuth } from '../../lib/auth';
import type { DenBanDo } from '../nema/LopDenNema';
import { NutNhay } from '../nema/NutNhay';

const so = (v: number | null | undefined, dv: string) => (v == null ? '—' : `${v} ${dv}`);
/* TK-47 (kiểm L6): ô số đo xếp theo bề rộng KHỐI (`auto-fit`, ô ≥ 4.5rem) — 5 cột cứng trong cột 340 px cắt "Độ sá…" / "460 …" */

function O({ nhan, gia, xam = false, canh = false }: { nhan: string; gia: string; xam?: boolean; canh?: boolean }) {
  return (
    <div className="o-noi min-w-0 px-2 py-1.5">
      <small className="block truncate text-xs text-chu-2">{nhan}</small>
      <b className={`block truncate text-base font-semibold tabular-nums ${xam || gia === '—' ? 'text-chu-3' : canh ? 'text-canh' : 'text-chu-1'}`}>{gia}</b>
    </div>
  );
}

/** Đèn có số đo hiện hành không (máy chủ tính `soDoHienHanh`; vắng ⇒ xét các số). */
export const coSoDo = (d: DenBanDo) => d.soDoHienHanh !== false && [d.dim, d.p_w, d.u_v, d.i_10ma, d.pf_pct].some((v) => v != null);

/** Lý do chưa có số — bỏ nếu trùng chữ của chip (một chữ một lần). */
function lyDoChuaCoSo(d: DenBanDo): string | null {
  const ly = d.nhanLenh === true ? 'chờ lần quét đầu' : chuLyDo(d.lyDo);
  return ly && ly !== NHAN_BADGE[d.badge].nhan ? ly : null;
}

const nutNho = 'ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-nut text-chu-2 hover:bg-nen-2 hover:text-chu-1';

/** KA-49 §4.2: đồng hồ kim U — cung gradient 0…300 V (mẫu `mau_van_hanh`). NULL ⇒ cung trống, "—" xám (luật số 0). */
export function DongHoU({ v, xam }: { v: number | null; xam: boolean }) {
  const id = useIdMien();
  const tl = v == null ? 0 : Math.max(0, Math.min(1, v / 300));
  const goc = Math.PI * tl;
  const x = (100 - 80 * Math.cos(goc)).toFixed(1), y = (100 - 80 * Math.sin(goc)).toFixed(1);
  return (
    <svg width="168" height="98" viewBox="0 0 200 116" role="img" aria-label={v == null ? 'Điện áp chưa có số' : `Điện áp ${v} V trên thang 0 đến 300`} data-o="dong-ho-u">
      <defs>
        <linearGradient id={`${id}-cung`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="rgb(var(--nhan-dam))" /><stop offset="1" stopColor="rgb(var(--nhan-sang))" /></linearGradient>
        <radialGradient id={`${id}-quat`} cx="0.5" cy="0.9" r="0.7"><stop offset="0" stopColor="rgb(var(--nhan))" stopOpacity="0.18" /><stop offset="1" stopColor="rgb(var(--nhan))" stopOpacity="0" /></radialGradient>
      </defs>
      <path d="M20 100 A80 80 0 0 1 180 100 Z" fill={`url(#${id}-quat)`} />
      <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="rgb(var(--lom))" strokeWidth="16" strokeLinecap="round" />
      <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="rgb(var(--vien))" strokeWidth="12" strokeLinecap="round" />
      {v != null && tl > 0 && <path d={`M20 100 A80 80 0 0 1 ${x} ${y}`} fill="none" stroke={xam ? 'rgb(var(--chu-3))' : `url(#${id}-cung)`} strokeWidth="12" strokeLinecap="round" />}
      <text x="100" y="88" textAnchor="middle" fill={v == null || xam ? 'rgb(var(--chu-3))' : 'rgb(var(--chu-1))'} fontSize="34" fontWeight="600">{v ?? '—'}</text>
      <text x="100" y="106" textAnchor="middle" fill="rgb(var(--chu-2))" fontSize="12">V</text>
      <text x="20" y="116" textAnchor="middle" fill="rgb(var(--chu-3))" fontSize="10">0</text>
      <text x="180" y="116" textAnchor="middle" fill="rgb(var(--chu-3))" fontSize="10">300</text>
    </svg>
  );
}

/** KA-49 §4.2: công suất 30 phút (miền gradient) + vạch lúc ra lệnh dim. Không có lịch sử ⇒ ẩn đồ thị, ghi "chưa có lịch sử". */
function CongSuat30({ cabinetId, addr, lenh }: { cabinetId: string; addr: number; lenh: { luc: string; muc: number | null } | null }) {
  const h = useNema24h(cabinetId, addr);
  const id = useIdMien();
  const tu = Date.now() + (lechGioMs() ?? 0) - 30 * 60_000;
  const ds = (h.data?.rows ?? []).map((r) => ({ t: new Date(r.ts).getTime(), p: r.p_w })).filter((r) => r.t >= tu);
  const vach = lenh && new Date(lenh.luc).getTime() >= tu ? new Date(lenh.luc).getTime() : null;
  return (
    <div className="op-p30" data-o="p-30-phut">
      <span className="op-p30-ten">Công suất 30 phút (W)</span>
      {ds.length >= 2 ? (
        <div className="h-[72px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ds} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              {defsMien(id, 1, ['--tim'])}
              <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} hide />
              <YAxis hide domain={[0, 'dataMax']} />
              <Tooltip {...KIEU_GOI_Y} labelFormatter={(v) => new Date(Number(v)).toLocaleTimeString('vi-VN')} formatter={(v) => [`${v} W`, 'P']} />
              {vach !== null && <ReferenceLine x={vach} stroke="rgb(var(--canh))" strokeDasharray="3 3"
                label={{ value: `lệnh ${lenh?.muc ?? '—'} %`, fill: 'rgb(var(--canh))', fontSize: 10, position: 'insideBottomRight' }} />}
              <Area type="linear" dataKey="p" stroke="rgb(var(--tim))" strokeWidth={2} fill={toMien(id, 0)} fillOpacity={1} connectNulls={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : <p className="m-0 text-xs text-chu-3" data-o="chua-co-lich-su">{h.isLoading ? 'Đang tải…' : 'Chưa có lịch sử 30 phút'}</p>}
    </div>
  );
}

type TabDen = 'dien' | 'gps' | 'dinh-danh';
const TEN_TAB: Record<TabDen, string> = { dien: 'Điện', gps: 'GPS', 'dinh-danh': 'Định danh' };

export function ODenChon({ d, onDong, onThu, className = '', lenh = null }: {
  d: DenBanDo; onDong?: () => void; onThu?: () => void; className?: string;
  /** KA-49: lệnh dim gần nhất của đèn (vạch trên đồ thị P · ô "Dim đèn báo" vàng khi lệch) */
  lenh?: { luc: string; muc: number | null } | null;
}) {
  const qd = useAuth().coCoTuyen('canControl', d.tuyenId ?? null);
  const tuQ = useNemaTu(d.cabinetId);
  const day = tuQ.data?.den.find((x) => x.addr === d.addr) ?? null;
  const [tab, setTab] = useState<TabDen>('dien');
  const b = NHAN_BADGE[d.badge];
  const ten = tenDen(d);
  const matMang = d.online !== true;
  const khoa = khoaLenhDen(d);
  const coSo = coSoDo(d);
  const ly = coSo ? null : lyDoChuaCoSo(d);
  const lechDim = lenh?.muc != null && d.dim != null && d.dim !== lenh.muc;
  const nguon = day?.src === 0 ? 'Đo thật' : day?.src === 1 ? 'Giả lập' : null;
  return (
    <section className={`op-chi-tiet-den flex flex-col gap-2 ${className}`} data-o="o-den-chon" data-den-chon={`${d.cabinetId}:${d.addr}`} aria-label={ten}>
      <div className="flex min-w-0 items-center gap-2">
        <b className="min-w-0 truncate text-base text-chu-1">{ten}</b>
        <span className="shrink-0 text-xs text-chu-2">{day?.rated_w != null ? `${day.rated_w} W` : '— W'}{d.nhomTb != null ? ` · nhóm ${d.nhomTb}` : ''}</span>
        <Badge tone={b.tone}>{b.nhan}</Badge>
        {(nguon || d.giaLap) && <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[11px] ${nguon === 'Đo thật' ? 'bg-nen-3 text-nhan-sang' : 'bg-canh-nen text-canh'}`} data-o="nguon-so">{nguon ?? 'GIẢ LẬP'}</span>}
        {onThu && (
          <button type="button" onClick={onThu} aria-label="Thu gọn" data-o="thu-o-den" className={nutNho}>
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {onDong && (
          <button type="button" onClick={onDong} aria-label="Đóng" data-o="dong-o-den" className={onThu ? nutNho.replace('ml-auto ', '') : nutNho}>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <p className="m-0 flex flex-wrap gap-x-1.5 text-xs text-chu-2">
        <span className="min-w-0 truncate">{d.tenTu}</span><span aria-hidden="true">·</span>
        <span className={d.online ? '' : 'text-chu-3'}>{d.online == null ? 'Mạng —' : d.online ? 'Trong mạng' : 'Mất mạng'}</span><span aria-hidden="true">·</span>
        <span>{d.nhanh == null ? 'Chưa gán nhánh' : `Ngõ ${d.nhanh}`}</span><span aria-hidden="true">·</span>
        <span className="whitespace-nowrap" data-o="luc-cap-nhat">Cập nhật {tuoiNgan(lucCapNhat(d))}</span>
      </p>
      <div className="op-tab-den" role="tablist" aria-label="Chi tiết đèn" data-o="tab-chi-tiet-den">
        {(Object.keys(TEN_TAB) as TabDen[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-tab-den={k}>{TEN_TAB[k]}</button>
        ))}
      </div>
      {tab === 'dien' && (
        <>
          {coSo ? (
            <div className="op-dien-den">
              <DongHoU v={d.u_v ?? null} xam={matMang} />
              <div className="grid grid-cols-[repeat(auto-fit,minmax(4.5rem,1fr))] gap-1.5" data-o="so-do-den">
                <O nhan="Dòng I" gia={so(iMa(d.i_10ma), 'mA')} xam={matMang} />
                <O nhan="Công suất P" gia={so(d.p_w, 'W')} xam={matMang} />
                <O nhan="Hệ số PF" gia={pfHai(d.pf_pct) ?? '—'} xam={matMang} />
                <O nhan="Dim đèn báo" gia={so(d.dim, '%')} xam={matMang} canh={lechDim} />
              </div>
            </div>
          ) : (
            <p className="m-0 text-sm text-chu-3" data-o="chua-co-so-do">Chưa có số đo{ly ? ` · ${ly}` : ''}</p>
          )}
          <div className="grid grid-cols-3 gap-2 text-xs text-chu-2" data-o="phu-den">
            <span>Tuổi số đo <b className="font-medium text-chu-1">{tuoiNgan(lucCapNhat(d))}</b></span>
            <span>Relay <b className="font-medium text-chu-1">{day?.relay == null ? '—' : day.relay === 1 ? 'Đang cắt' : 'Không cắt'}</b></span>
            <span>Khung lỗi <b className="font-medium text-chu-1">{day?.khung ? `${day.khung.loi} / ${day.khung.ok + day.khung.loi}` : '—'}</b></span>
          </div>
          <CongSuat30 cabinetId={d.cabinetId} addr={d.addr} lenh={lenh} />
        </>
      )}
      {tab === 'gps' && (
        <dl className="op-dl-den" data-o="gps-den">
          <dt>Kết quả</dt><dd>{day?.gps.result == null ? '—' : day.gps.result === 0 ? 'Đọc được' : day.gps.result === 1 ? 'Không trả lời' : 'Không có trong mạng'}</dd>
          <dt>Toạ độ GPS</dt><dd>{day?.gps.lat_e7 != null && day.gps.lon_e7 != null ? `${(day.gps.lat_e7 / 1e7).toFixed(5)}, ${(day.gps.lon_e7 / 1e7).toFixed(5)}` : '—'}</dd>
          <dt>Lần đọc</dt><dd>{tuoiNgan(day?.gps.at ?? null)}</dd>
          <dt>Vị trí trên bản đồ</dt><dd>{d.pos ? `${d.pos.lat.toFixed(5)}, ${d.pos.lng.toFixed(5)}${d.pos.src === 'KEO_TAY' ? ' · kéo tay' : ''}` : 'Chưa xếp vị trí'}</dd>
        </dl>
      )}
      {tab === 'dinh-danh' && (
        <dl className="op-dl-den" data-o="dinh-danh-den">
          <dt>Địa chỉ</dt><dd className="tabular-nums">{d.addr.toString(16).toUpperCase().padStart(4, '0')}</dd>
          <dt>IID</dt><dd className="tabular-nums">{day?.iid ?? '—'}</dd>
          <dt>Tủ</dt><dd>{d.tenTu}</dd>
          <dt>Trụ</dt><dd>{day?.pole_no ?? '—'}</dd>
          <dt>Công suất định mức</dt><dd>{day?.rated_w != null ? `${day.rated_w} W` : '—'}</dd>
          <dt>Nhóm thiết bị</dt><dd>{d.nhomTb ?? '—'}</dd>
        </dl>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <NutNhay cabinetId={d.cabinetId} addr={d.addr} ten={ten} khoaVi={khoa.dim} qd={qd} trungTinh hienLyDo={false} />
        <Link to={`/den-nema?tu=${d.cabinetId}&den=${d.addr}`} className="ml-auto text-sm text-nhan hover:underline" data-o="mo-quan-ly-den">Mở trong Quản lý đèn</Link>
      </div>
    </section>
  );
}

/** TK-46 §2: tấm trượt THU GỌN — một hàng: tên · trạng thái · % · nút mở rộng. */
export function ODenGon({ d, onMo, onDong }: { d: DenBanDo; onMo: () => void; onDong?: () => void }) {
  const b = NHAN_BADGE[d.badge];
  return (
    <section className="flex min-w-0 items-center gap-2 rounded-the border border-vien bg-nen-1 px-3 py-2" data-o="o-den-gon" data-den-chon={`${d.cabinetId}:${d.addr}`} aria-label={tenDen(d)}>
      <b className="min-w-0 truncate text-sm text-chu-1">{tenDen(d)}</b>
      <Badge tone={b.tone}>{b.nhan}</Badge>
      <span className="shrink-0 text-sm tabular-nums text-chu-2">{d.dim == null ? '— %' : `${d.dim} %`}</span>
      <button type="button" onClick={onMo} aria-label="Mở rộng" data-o="mo-o-den" className={nutNho}><ChevronUp className="h-4 w-4" aria-hidden="true" /></button>
      {onDong && <button type="button" onClick={onDong} aria-label="Đóng" className={nutNho.replace('ml-auto ', '')}><X className="h-4 w-4" aria-hidden="true" /></button>}
    </section>
  );
}
